import { DocumentNode, gql, PureQueryOptions, useMutation, useQuery } from '@apollo/client';
import { useNavigate, useParams } from 'react-router-dom';
import { CommonResources } from '../../../config/CommonResources';
import { ActionType, DataType, EditionMode, ViewType } from '../../../config/Constants';
import { IConfigProps, IDisplayProps, IQueryProps, IWhereProps } from '../../services/GraphQLShared';
import { EntityType, EntityTypeProps } from '../../services/GraphQLModel';
import BreadcrumbBar, { IBreadcrumbProps, ILinkProps, IToolbarProps } from '../BreadcrumbBar';
import { Container, Row, Col } from 'react-bootstrap';
import { ErrorPanel, GenericLoadingPanel } from './Panels';
import { isDevelopment, Nullable } from '../../helpers/Utils';
import { ConfirmDialog, IDialogProps } from './ConfirmDialog';
import { Dialog } from '@progress/kendo-react-dialogs';
import { useSecurity } from '../../helpers/SecurityHooks';
import { TabStrip, TabStripSelectEventArguments, TabStripTab } from '@progress/kendo-react-layout';
import { Upload, UploadOnBeforeUploadEvent } from '@progress/kendo-react-upload';
import { EntityStatusType, IEntityStatusType } from '../../../config/Enums';
import { Button } from '@progress/kendo-react-buttons';
import { IFieldBoxProps } from '../boxes/FieldBox';
import TabContainer from '../../shared/TabContainer';
import { DisplayBox } from '../boxes/DisplayBox';
import { useAtom, WritableAtom } from 'jotai';
import { useState } from 'react';
import { AvatarBox } from '../boxes/StatusBox';
import React from 'react';
import useAppModel, { MutationTypeEnum, QueryTypeEnum } from '../../helpers/ModelHook';
import { getter } from '@progress/kendo-react-common';
import { RESET } from 'jotai/vanilla/utils';
import { apiConfig } from '../../../config/Config';
type SetStateActionWithReset<Value> = Value | typeof RESET | ((prev: Value) => Value | typeof RESET);

export interface IFormPageProps<T extends EntityTypeProps> {
  editionMode: EditionMode;
  initialValues: T;
  callBackSubmit: (dataItem: any) => void;
  callBackCancel: () => void;
}
export interface ICreateDialogProps extends IDialogProps {
  parent?: IDisplayProps;
  width?: number;
}
export interface IUpdateDialogProps extends IDialogProps {
  dataItem: any;
  width?: number;
}
export interface IParameter extends IDisplayProps {
  type: EntityType;
}
export interface IParameterAccessor {
  accessor: string;
  name: string;
}
export interface IParameterList {
  name: string;
  value: any;
}
export interface IPanelTabProps {
  parameter: IParameter;
  parameters?: IParameterList[];
  readOnly?: boolean;
}
export interface IToolbarPanelProps<T extends EntityTypeProps> {
  title: string;
  type: ActionType;
  handleClick: (id: string) => void;
  condition?: (dataItem: T) => boolean;
}
export interface IViewPanelTabProps<T extends EntityTypeProps> {
  title: string;
  component: (props: IPanelTabProps) => React.ReactElement<IPanelTabProps>;
  condition?: (dataItem: T) => boolean;
  readOnly?: (dataItem: T) => boolean;
  parameters?: IParameterAccessor[];
}
export interface ICreatePanelProps<T extends EntityTypeProps> {
  config: IConfigProps;
  mutation?: DocumentNode;
  filter?: IWhereProps[];
  defaultQueries?: Array<string | PureQueryOptions>;
  component: (props: IFormPageProps<T>) => React.ReactElement<IFormPageProps<T>>;
  getInitialValues: (dataitem: any) => T;
  getMutation: (dataitem: T, mode: EditionMode) => any;
}
export interface IViewPanelProps<T extends EntityTypeProps> {
  config: IConfigProps;
  query?: IQueryProps;
  mutation?: DocumentNode;
  filter?: IWhereProps[];
  displayBoxes: IFieldBoxProps[];
  toolbar?: IToolbarPanelProps<T>[];
  tabs?: IViewPanelTabProps<T>[];
  tabSelectedAtom?: WritableAtom<number, [SetStateActionWithReset<number>], void>;
  editionModeAtom?: WritableAtom<EditionMode, [SetStateActionWithReset<EditionMode>], void>;
  refetchQueries?: (id: Nullable<string>) => Array<string | PureQueryOptions>;
  component: (props: IFormPageProps<T>) => React.ReactElement<IFormPageProps<T>>;
  getInitialValues: (dataitem: any) => T;
  getMutation: (dataitem: T, mode: EditionMode) => any;
  onClickEdit?: (dataitem: any, isDefault: () => void) => void;
}
export interface ICreateDialogPanelProps<T extends EntityTypeProps> extends ICreateDialogProps, ICreatePanelProps<T> {
  parentAccessor?: string;
}
export interface IUpdateDialogPanelProps<T extends EntityTypeProps> extends IUpdateDialogProps, ICreatePanelProps<T> {}
export function CreatePanel<T extends EntityTypeProps>(props: ICreatePanelProps<T>): React.ReactElement {
  const navigate = useNavigate();
  const { buildMutation, defaultRefetchQueries } = useAppModel();
  const mutation = props.mutation ?? buildMutation(props.config, MutationTypeEnum.Create).query;
  const [mutationCreate, { loading: mutationCreateLoading, error: mutationCreateError }] = useMutation(mutation, {
    refetchQueries: props.defaultQueries
      ? [...props.defaultQueries, ...defaultRefetchQueries(props.config, null)]
      : defaultRefetchQueries(props.config, null),
    awaitRefetchQueries: false,
    onError(error) {
      if (isDevelopment) console.error('error:>> ', error.message);
    },
    update(cache, { data }) {
      if (data[`create${props.config.entityName}`]) {
        const newItem = data[`create${props.config.entityName}`].id;
        const path = `${props.config.path}/${newItem}`;
        navigate(path);
      }
    },
  });
  const handleCallback = (dataItem: T) => mutationCreate(props.getMutation(dataItem, EditionMode.Create));
  const dataBreadcrumb: IBreadcrumbProps = {
    data: {
      Type: ViewType.View,
      Toolbar: [
        {
          Type: ActionType.Cancel,
          Title: CommonResources.Button_Back,
          handleClick: () => navigate(-1),
          Visible: true,
        },
      ],
      Links: [
        { Title: props.config.area, Active: false },
        { Title: props.config.displayPlural, Path: props.config.path, Active: false },
        { Title: CommonResources.BreadCrumb_New, Active: true },
      ],
      Filters: [],
    },
  };
  const initialValues = (): T => props.getInitialValues(null);
  return (
    <>
      <BreadcrumbBar data={dataBreadcrumb.data} />
      <Container className="mt-3">
        <Row>
          <Col>
            <props.component
              editionMode={EditionMode.Create}
              initialValues={initialValues()}
              callBackSubmit={handleCallback}
              callBackCancel={() => navigate(-1)}
            />
            <GenericLoadingPanel loading={mutationCreateLoading} />
            <ErrorPanel error={mutationCreateError} />
          </Col>
        </Row>
      </Container>
    </>
  );
}
interface IChangeStatusProps {
  id: Nullable<string>;
  config: IConfigProps;
  changeStatus: IEntityStatusType | undefined;
  onCompleted: () => void;
  refetchQueries: (id: Nullable<string>) => Array<string | PureQueryOptions>;
}
export function ChangeStatusDialog(props: IChangeStatusProps): React.ReactElement | null {
  const [changeStatus, setChangeStatus] = React.useState<IEntityStatusType | undefined>(props.changeStatus);
  const [messageConfirm, setMessageConfirm] = React.useState(CommonResources.Dialog_MessageDelete);
  const MUTATION_CHANGE_STATUS = gql`mutation ChangeStatus${props.config.entityName} ($id: ID!, $status: EntityStatus!) { changeStatus${props.config.entityName}(id: $id, status : $status) { message }}`;
  const [mutationChangeStatus] = useMutation(MUTATION_CHANGE_STATUS, {
    refetchQueries: props.refetchQueries(props.id),
    awaitRefetchQueries: true,
  });
  const handleDelete = () => mutationChangeStatus({ variables: { id: props.id, status: EntityStatusType.Inactive } });
  const handleActive = () => mutationChangeStatus({ variables: { id: props.id, status: EntityStatusType.Active } });
  const handleDisable = () => mutationChangeStatus({ variables: { id: props.id, status: EntityStatusType.Disable } });
  const handleConfirmOK = () => {
    if (changeStatus === EntityStatusType.Inactive) handleDelete();
    if (changeStatus === EntityStatusType.Active) handleActive();
    if (changeStatus === EntityStatusType.Disable) handleDisable();
    handleCancel();
  };
  const handleCancel = () => {
    setChangeStatus(undefined);
    props.onCompleted();
  };
  React.useEffect(() => {
    setChangeStatus(props.changeStatus);
    switch (props.changeStatus) {
      case EntityStatusType.Active:
        setMessageConfirm(`${CommonResources.Dialog_MessageActivate}${props.config.display.toLowerCase()}?`);
        break;
      case EntityStatusType.Disable:
        setMessageConfirm(`${CommonResources.Dialog_MessageDisable}${props.config.display.toLowerCase()}?`);
        break;
      case EntityStatusType.Disable:
        setMessageConfirm(`${CommonResources.Dialog_MessageDelete}${props.config.display.toLowerCase()}?`);
        break;
    }
  }, [props.changeStatus]);
  if (!props.id || !changeStatus) return null;
  return (
    <ConfirmDialog
      title={CommonResources.Dialog_ConfirmChangeStatusTitle}
      message={messageConfirm}
      description={CommonResources.Dialog_ConfirmClick}
      onClickOk={handleConfirmOK}
      onClickCancel={handleCancel}
      visible={true}
    />
  );
}
export function ViewPanel<T extends EntityTypeProps>(props: IViewPanelProps<T>): React.ReactElement {
  const navigate = useNavigate();
  const { buildQuery, buildMutation, defaultRefetchQueries } = useAppModel();
  const mutation = props.mutation ?? buildMutation(props.config, MutationTypeEnum.Update).query;
  const query = props.query ?? buildQuery(props.config, QueryTypeEnum.GetById);
  const defaultQueries = (id: string | null = null): Array<string | PureQueryOptions> =>
    props.refetchQueries
      ? [...props.refetchQueries(id), ...defaultRefetchQueries(props.config, id)]
      : defaultRefetchQueries(props.config, id);
  const currentSecurity = useSecurity(props.config.type, ViewType.View);
  const { id } = useParams();
  const { loading, error, data } = useQuery(query.query, { variables: { id: id } });
  const getName = () => (loading || error ? '' : data[props.config.queryIdNode][props.config.displayAccessor]);
  const getElement = (): T => (loading || error ? {} : data[props.config.queryIdNode]);
  const getParameter = (): IParameter =>
    loading || error
      ? { id: id, display: '', type: props.config.type }
      : {
          id: data[props.config.queryIdNode].id,
          display: data[props.config.queryIdNode][props.config.displayAccessor],
          type: props.config.type,
        };
  const [changeStatus, setChangeStatus] = React.useState<IEntityStatusType | undefined>(undefined);
  const [tabSelected, setTabSelected] = props.tabSelectedAtom ? useAtom(props.tabSelectedAtom) : useState(0);
  const handleTabSelect = (e: TabStripSelectEventArguments) => setTabSelected(e.selected);
  const [editionMode, setEditionMode] = props.editionModeAtom ? useAtom(props.editionModeAtom) : useState(EditionMode.Detail);
  const handleEditionMode = (mode: EditionMode) => {
    setEditionMode(mode);
  };
  const [mutationUpdate, { loading: mutationUpdateLoading, error: mutationUpdateError }] = useMutation(mutation, {
    refetchQueries: defaultQueries(id),
    awaitRefetchQueries: false,
    onError(error) {
      if (isDevelopment) console.error('error:>> ', error.message);
    },
    update(cache, { data }) {
      const node = data[`update${props.config.entityName}`].id;
      cache.writeQuery({
        query: query.query,
        variables: { id: id },
        data: { [query.queryNode]: node },
      });
      handleEditionMode(EditionMode.Detail);
    },
  });
  const handleDelete = () => setChangeStatus(EntityStatusType.Inactive);
  const handleActive = () => setChangeStatus(EntityStatusType.Active);
  const handleDisable = () => setChangeStatus(EntityStatusType.Disable);
  const valueOf = (field: string) => {
    if (loading || error) return '';
    const valueGetter = getter(field);
    return loading ? '' : valueGetter(data[props.config.queryIdNode]);
  };

  const getLinks = (): ILinkProps[] => {
    if (props.config.parentPath && props.config.parentDisplayAccessor && props.config.parentIdAccessor) {
      return [
        { Title: props.config.area, Active: false },
        {
          Title: valueOf(props.config.parentDisplayAccessor),
          Path: `${props.config.parentPath}/${valueOf(props.config.parentIdAccessor)}`,
          Active: false,
        },
        { Title: props.config.displayPlural, Path: props.config.path, Active: false },
        { Title: valueOf(props.config.displayAccessor), Path: `${props.config.path}/${id}`, Active: false },
        { Title: CommonResources.BreadCrumb_View, Active: true },
      ];
    }
    return [
      { Title: props.config.area, Active: false },
      { Title: props.config.displayPlural, Path: props.config.path, Active: false },
      { Title: valueOf(props.config.displayAccessor), Path: `${props.config.path}/${id}`, Active: false },
      { Title: CommonResources.BreadCrumb_View, Active: true },
    ];
  };
  const getToolbar = (): IToolbarProps[] => {
    const result = [
      { Type: ActionType.Cancel, Title: CommonResources.Button_Back, handleClick: () => navigate(-1), Visible: true },
      {
        Type: ActionType.ShowStatus,
        Title: CommonResources.Button_ShowStatus,
        handleClick: () => null,
        Visible: loading ? false : true,
      },
    ];
    props.toolbar
      ?.filter((e) => !e.condition || e.condition(getElement()))
      .forEach((e: IToolbarPanelProps<T>) => {
        result.push({
          Type: e.type,
          Title: e.title,
          handleClick: () => e.handleClick(id ?? ''),
          Visible: loading ? false : currentSecurity.canUpdate && data[props.config.queryIdNode].status == EntityStatusType.Active,
        });
      });
    result.push({
      Type: ActionType.Disable,
      Title: CommonResources.Button_Disable,
      handleClick: handleDisable,
      Visible: loading ? false : currentSecurity.canDelete && data[props.config.queryIdNode].status == EntityStatusType.Active,
    });
    result.push({
      Type: ActionType.Undelete,
      Title: CommonResources.Button_Activate,
      handleClick: handleActive,
      Visible: loading ? false : currentSecurity.canUpdate && data[props.config.queryIdNode].status != EntityStatusType.Active,
    });
    result.push({
      Type: ActionType.Delete,
      Title: CommonResources.Button_Delete,
      handleClick: handleDelete,
      Visible: loading ? false : currentSecurity.canDelete && data[props.config.queryIdNode].status != EntityStatusType.Inactive,
    });
    return result;
  };
  const dataBreadcrumb: IBreadcrumbProps = {
    data: {
      Type: ViewType.View,
      Status: valueOf('status'),
      Toolbar: getToolbar(),
      Links: getLinks(),
      Filters: [],
    },
  };
  const handleEditDefault = () => setEditionMode(EditionMode.Update);
  const handleOnClickEdit = () => (props?.onClickEdit ? props.onClickEdit(getElement(), handleEditDefault) : handleEditDefault());
  const GetAvatarBox = () => {
    switch (props.config.type) {
      case EntityType.Market:
        return (
          <AvatarBox
            size="large"
            text={data[props.config.queryIdNode][props.config.displayAccessor]}
            isoCode={data[props.config.queryIdNode].isoCode}
          />
        );
      case EntityType.EmbedReport:
        return (
          <AvatarBox
            size="large"
            text={data[props.config.queryIdNode][props.config.displayAccessor]}
            isoCode={data[props.config.queryIdNode].market?.isoCode}
            id={data[props.config.queryIdNode].id}
          />
        );
      default:
        return <AvatarBox text={data[props.config.queryIdNode][props.config.displayAccessor]} size="large" />;
    }
  };
  const onBeforeUploadIcon = (event: UploadOnBeforeUploadEvent) => {
    event.additionalData.reportId = data[props.config.queryIdNode].id;
  };
  const DisplayPanel = () => {
    return (
      <>
        <Row>
          <Col md={2} className="d-flex justify-content-center">
            <GetAvatarBox />
          </Col>
          <Col md={10}>
            {props.displayBoxes.map((box, i) => (
              <DisplayBox
                {...box}
                key={i}
                value={valueOf(box.field)}
                icon={box.icon}
                route={box.route ? `${box.route}/${valueOf(box.field.replace('display', 'id'))}` : ''}
                type={box.type ?? DataType.String}
              />
            ))}
          </Col>
        </Row>
        {currentSecurity.canUpdate && (
          <Row>
            <Col className="mt-2 d-flex justify-content-end">
              <Button themeColor="primary" onClick={handleOnClickEdit}>
                {CommonResources.Button_Edit}
              </Button>
            </Col>
          </Row>
        )}
        <Row style={{ marginTop: '1rem' }}>
          <Col md={2} >
          </Col>
          <Col>
            <h4>
              Select an image file if you want to replace application icon:
            </h4>
            <Upload
              defaultFiles={[]}
              batch={false}
              multiple={false}
              onBeforeUpload={onBeforeUploadIcon}
              saveUrl={`${apiConfig.resourceUri}/EmbedReport/saveicon`}
            />
          </Col>
        </Row>
      </>
    );
  };
  const handleCallback = (dataItem: any) => mutationUpdate(props.getMutation(dataItem, EditionMode.Update));
  const ViewForm = () => {
    if (editionMode === EditionMode.Detail)
      return (
        <Container>
          <DisplayPanel />
        </Container>
      );
    return (
      <Container>        
        <props.component
          editionMode={EditionMode.Update}
          initialValues={props.getInitialValues(data[props.config.queryIdNode])}
          callBackSubmit={handleCallback}
          callBackCancel={() => handleEditionMode(EditionMode.Detail)}
        />
        <ErrorPanel error={mutationUpdateError} />
        <GenericLoadingPanel loading={mutationUpdateLoading} />
      </Container>
    );
  };
  if (loading) return <GenericLoadingPanel loading={loading} />;
  if (error) return <ErrorPanel error={error} />;
  return (
    <>
      <BreadcrumbBar data={dataBreadcrumb.data} />
      <Container fluid className="mt-3 px-4">
        <Row>
          <TabContainer title={getName()}>
            <TabStrip selected={tabSelected} onSelect={handleTabSelect}>
              <TabStripTab title={CommonResources.Tab_Title}>
                <ViewForm />
              </TabStripTab>
              {props.tabs
                ?.filter((e) => !e.condition || e.condition(getElement()))
                .map((tab: IViewPanelTabProps<T>, i: number) => (
                  <TabStripTab key={i} title={tab.title}>
                    <tab.component
                      parameter={getParameter()}
                      parameters={tab.parameters?.map((param: any) => Object.assign({ value: valueOf(param.accessor), name: param.name }))}
                      readOnly={tab.readOnly ? tab.readOnly(getElement()) : undefined}
                    />
                  </TabStripTab>
                ))}
            </TabStrip>
          </TabContainer>
        </Row>
      </Container>
      <ChangeStatusDialog
        id={id}
        config={props.config}
        changeStatus={changeStatus}
        onCompleted={() => setChangeStatus(undefined)}
        refetchQueries={defaultQueries}
      />
    </>
  );
}

export function CreateDialogPanel<T extends EntityTypeProps>(props: ICreateDialogPanelProps<T>): React.ReactElement | null {
  if (!props.visible) return null;
  const { buildMutation, defaultRefetchQueries } = useAppModel();
  const mutation = props.mutation ?? buildMutation(props.config, MutationTypeEnum.Create).query;
  const [mutationCreate, { loading: mutationCreateLoading, error: mutationCreateError }] = useMutation(mutation, {
    refetchQueries: props.defaultQueries && props.defaultQueries.length > 0
      ? [...props.defaultQueries, ...defaultRefetchQueries(props.config, null)]
      : [],
    awaitRefetchQueries: false,
    onError(error) {
      console.error('error:>> ', error.message);
    },
    update(cache, result) {
      if (result.errors && result.errors.length > 0) {
        return;
      }
      props.onClickOk();
    },
  });
  const handleCallback = (dataItem: any) => mutationCreate(props.getMutation(dataItem, EditionMode.Create));
  const initialValues = (): T => {
    const data = props.getInitialValues(null);
    if (props.parentAccessor) data[props.parentAccessor] = props.parent;
    return data;
  };

  return (
    <Dialog title={`${CommonResources.Dialog_Create} ${props.config.display}`} width={props.width ?? 800} onClose={props.onClickCancel}>
      <div style={{ maxHeight: '85vh'}}>
      <props.component
        editionMode={EditionMode.CreateDialog}
        initialValues={initialValues()}
        callBackSubmit={handleCallback}
        callBackCancel={props.onClickCancel}
      />
      </div>
      <GenericLoadingPanel loading={mutationCreateLoading} container="k-dialog" />
      <ErrorPanel error={mutationCreateError} />
    </Dialog>
  );
}

export function UpdateDialogPanel<T extends EntityTypeProps>(props: IUpdateDialogPanelProps<T>): React.ReactElement | null {
  if (!props.visible) return null;
  if (!props.dataItem) return null;
  const { buildMutation, defaultRefetchQueries } = useAppModel();
  const mutation = props.mutation ?? buildMutation(props.config, MutationTypeEnum.Update).query;
  const [mutationUpdate, { loading: mutationUpdateLoading, error: mutationUpdateError }] = useMutation(mutation, {
    refetchQueries: props.defaultQueries
      ? [...props.defaultQueries, ...defaultRefetchQueries(props.config, props.dataItem.id)]
      : defaultRefetchQueries(props.config, props.dataItem.id),
    awaitRefetchQueries: false,
    onError(error) {
      if (isDevelopment) console.error('error:>> ', error.message);
    },
    update() {
      props.onClickOk();
    },
  });
  const handleCallback = (dataItem: any) => mutationUpdate(props.getMutation(dataItem, EditionMode.Update));
  return (
    <Dialog title={`${CommonResources.Dialog_Update} ${props.config.display}`} width={props.width ?? 800} onClose={props.onClickCancel}>
      <div className="k-panel" style={{ maxHeight: '85vh', borderWidth: '0' }}>
        <props.component
          editionMode={EditionMode.UpdateDialog}
          initialValues={props.dataItem}
          callBackSubmit={handleCallback}
          callBackCancel={props.onClickCancel}
        />
      </div>
      <GenericLoadingPanel loading={mutationUpdateLoading} container="k-dialog" />
      <ErrorPanel error={mutationUpdateError} />
    </Dialog>
  );
}
